<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import type { PlatformEvent, PlatformStats, PlaygroundScores } from '@/types/api';
import { PLAYGROUND_CATEGORIES } from '@/types/api';
import { fetchMonthEvents, fetchPlatformStats, fetchPlaygroundTotals } from '@/lib/apiClient';
import { formatDateKeyRu, parseDateKey } from '@/lib/dateKey';
import { useAuth } from '@/composables/useAuth';

const router = useRouter();
const { token } = useAuth();
const loading = ref(true);
const events = ref<PlatformEvent[]>([]);
const stats = ref<PlatformStats | null>(null);
const wishTotals = ref<PlaygroundScores | null>(null);

const wishTiles = computed(() =>
  (['money', 'health', 'love'] as const).map((key) => ({
    key,
    ...PLAYGROUND_CATEGORIES[key],
    value: wishTotals.value?.[key] ?? 0,
  })),
);

const countdown = computed(() => {
  if (!stats.value?.nextEvent) return null;
  const target = parseDateKey(stats.value.nextEvent.eventDate);
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return 'Сегодня праздник!';

  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return `До события «${stats.value.nextEvent.title}» осталось ${days} дн.`;
});

onMounted(async () => {
  if (!token.value) {
    loading.value = false;
    return;
  }

  try {
    const [eventsResponse, statsResponse, totalsResponse] = await Promise.all([
      fetchMonthEvents(token.value),
      fetchPlatformStats(token.value),
      fetchPlaygroundTotals(token.value),
    ]);
    events.value = eventsResponse.events;
    stats.value = statsResponse;
    wishTotals.value = totalsResponse.totals;
  } finally {
    loading.value = false;
  }
});

function openTriWishes() {
  router.push({ name: 'tri-wishes' });
}
</script>

<template>
  <div class="page">
    <section class="page__hero">
      <Tag value="Главная" severity="danger" rounded />
      <h1>Ну, ты снова тут</h1>
      <p>Стена, подарки, кликер — разбирайся по ходу, мы тоже так делали.</p>
    </section>

    <ProgressSpinner v-if="loading" />

    <template v-else>
      <section v-if="wishTotals" class="wishes">
        <div class="wishes__header">
          <div>
            <p class="wishes__eyebrow">Игра «Три Желания»</p>
            <h2 class="wishes__title">Мы нажелали Алле</h2>
            <p class="wishes__subtitle">
              Каждый клик гостей — это пожелание денег, здоровья и любви имениннице.
              Накопленные пожелания станут силой магического отряда в «Лес Трёх Желаний», когда Божественный Создатель откроет портал.
            </p>
          </div>
          <Button
            label="Пожелать ещё"
            icon="pi pi-bolt"
            size="small"
            @click="openTriWishes"
          />
        </div>

        <div class="wishes__grid">
          <article
            v-for="tile in wishTiles"
            :key="tile.key"
            class="wishes__tile"
            :style="{ background: tile.gradient, borderColor: `${tile.color}44` }"
          >
            <span class="wishes__tile-emoji">{{ tile.emoji }}</span>
            <strong class="wishes__tile-value">{{ tile.value.toLocaleString('ru-RU') }}</strong>
            <span class="wishes__tile-label">{{ tile.label }}</span>
          </article>
        </div>

        <p v-if="wishTotals.total > 0" class="wishes__foot">
          Всего пожеланий от гостей:
          <strong>{{ wishTotals.total.toLocaleString('ru-RU') }}</strong>
        </p>
      </section>

      <Card v-if="countdown" class="page__countdown">
        <template #content>
          <div class="countdown">
            <span class="countdown__emoji">⏳</span>
            <strong>{{ countdown }}</strong>
          </div>
        </template>
      </Card>

      <section class="page__stats" v-if="stats">
        <Card><template #content><strong>{{ stats.postsCount }}</strong><span>Сообщений на стене</span></template></Card>
        <Card><template #content><strong>{{ stats.membersCount }}</strong><span>Участников</span></template></Card>
        <Card><template #content><strong>{{ stats.giftsCount }}</strong><span>Подарков</span></template></Card>
      </section>

      <section>
        <h2>События месяца</h2>
        <div v-if="events.length === 0" class="page__empty">Пока нет событий — администратор скоро добавит первое.</div>
        <div v-else class="events-grid">
          <Card v-for="event in events" :key="event.id" class="event-card">
            <template #title>
              <div class="event-card__title">
                <span class="event-card__emoji">{{ event.emoji }}</span>
                <span>{{ event.title }}</span>
              </div>
            </template>
            <template #content>
              <p class="event-card__date">{{ formatDateKeyRu(event.eventDate) }}</p>
              <p v-if="event.description" class="event-card__desc">{{ event.description }}</p>
            </template>
          </Card>
        </div>
      </section>
    </template>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.page__hero h1 {
  margin: 0.75rem 0 0.5rem;
  color: #831843;
}

.page__hero p {
  margin: 0;
  color: #9d174d;
}

.wishes {
  padding: 1.35rem 1.5rem;
  border-radius: 1.35rem;
  background: linear-gradient(135deg, #fff1f2 0%, #fdf2f8 50%, #faf5ff 100%);
  border: 2px solid #fbcfe8;
  box-shadow: 0 12px 32px rgb(219 39 119 / 10%);
}

.wishes__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.wishes__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.8rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #db2777;
  font-weight: 700;
}

.wishes__title {
  margin: 0 0 0.4rem;
  font-size: clamp(1.5rem, 4vw, 2rem);
  color: #831843;
}

.wishes__subtitle {
  margin: 0;
  max-width: 36rem;
  color: #9d174d;
  line-height: 1.5;
}

.wishes__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.wishes__tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 1.25rem 1rem;
  border-radius: 1.15rem;
  border: 2px solid;
  text-align: center;
  transition: transform 0.15s ease;
}

.wishes__tile:hover {
  transform: translateY(-2px);
}

.wishes__tile-emoji {
  font-size: 2rem;
  line-height: 1;
}

.wishes__tile-value {
  font-size: clamp(1.6rem, 4vw, 2.1rem);
  color: #831843;
  line-height: 1.1;
}

.wishes__tile-label {
  color: #9d174d;
  font-weight: 700;
  font-size: 0.95rem;
}

.wishes__foot {
  margin: 1rem 0 0;
  text-align: center;
  color: #be185d;
}

.wishes__foot strong {
  color: #db2777;
  font-size: 1.1rem;
}

.page__countdown :deep(.p-card-body) {
  background: linear-gradient(135deg, #fff1f2, #fce7f3);
}

.countdown {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #be185d;
  font-size: 1.1rem;
}

.countdown__emoji {
  font-size: 1.6rem;
}

.page__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.page__stats strong {
  display: block;
  font-size: 1.8rem;
  color: #db2777;
}

.page__stats span {
  color: #9d174d;
}

.events-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
}

.event-card__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.event-card__emoji {
  font-size: 1.5rem;
}

.event-card__date {
  color: #db2777;
  font-weight: 700;
}

.event-card__desc,
.page__empty {
  color: #9d174d;
}

@media (max-width: 768px) {
  .wishes__header {
    flex-direction: column;
  }

  .wishes__grid,
  .page__stats {
    grid-template-columns: 1fr;
  }
}
</style>
